import {
  INGEST_MAX_BYTES,
  INGEST_MAX_ITEMS,
  type IngestBatch,
  type IngestItem,
  type IngestPart,
  type IngestRecording,
} from '@voxa/shared';
import { RetryBudget } from './backoff.ts';

/**
 * The background channel to the server (task 4): the pipe tasks 5 and 6 will
 * eventually push transcripts and recording references down.
 *
 * It exists beside `telemetry.ts` rather than inside it because the two have
 * opposite shapes. Telemetry is a steady trickle of numbers on a ten-second
 * timer, and its whole safety argument is that there is nowhere in it to put
 * anything bigger. What the recording work needs is fewer, larger payloads,
 * more of them near the end of a call than at the start, and delivery that
 * keeps trying without ever becoming something the call waits on.
 *
 * The rule from `telemetry.ts` carries over unchanged and is the reason for
 * most of what follows: **losing a payload is cheaper than delaying a call.**
 * Nothing here is awaited by signalling, by the WebRTC handshake or by the
 * subtitle render. `part()` and `recording()` push onto an array and return;
 * everything expensive — serialising, measuring, sending, retrying — happens
 * on a later turn of the event loop, and every failure is swallowed.
 *
 * What it does not promise: delivery. There is no acknowledgement the caller
 * can wait for, no persistence across a reload, and no ordering guarantee once
 * a batch has been retried. A page that is closed while the network is down
 * loses what it was holding. That is the trade accepted on purpose; the
 * alternative is a call that stutters because a statistics endpoint is slow.
 *
 * Section 9, explicitly: **this does not carry speech today.** The shared
 * schema has a transcript member so that the shape is settled in the open, and
 * the server refuses it until task 8 — the consent screen both participants
 * see — exists. This class deliberately exposes no method that takes text, so
 * there is no path from a subtitle to the network even by accident. Adding one
 * before that screen ships is the mistake this comment is here to prevent.
 */

/** What should happen to a batch that has been handed to the network. */
export type SendOutcome =
  /** The server took it. Forget it. */
  | 'delivered'
  /** A blip or an outage: keep the items, wait longer, try again. */
  | 'retry'
  /** The server will never accept this batch. Keeping it is a leak. */
  | 'rejected';

export interface OutboxTransport {
  post(body: string): Promise<SendOutcome>;
  /** The last-gasp path. Synchronous, fire-and-forget; false means not taken. */
  beacon(body: string): boolean;
}

const PATH = '/api/ingest';

/** Measured in bytes, not characters: the server's limit is bytes, and one
 *  Cyrillic transcript is two of them per character. */
const encoder = new TextEncoder();
const byteLength = (s: string): number => encoder.encode(s).length;

export const browserTransport: OutboxTransport = {
  async post(body: string): Promise<SendOutcome> {
    let res: Response;
    try {
      res = await fetch(PATH, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body,
        // Lets a send that was already in flight finish after the page is
        // gone. The browser's keepalive budget is 64 KiB across all in-flight
        // requests, which the batch ceiling and the one-flush-at-a-time rule
        // together keep us under.
        keepalive: true,
      });
    } catch {
      // No response at all: offline, DNS, a proxy. Worth trying again.
      return 'retry';
    }
    if (res.ok) return 'delivered';
    // 429 and 5xx are the server asking for time. Everything else is the
    // server saying this particular batch is wrong, and it will be just as
    // wrong in thirty seconds.
    if (res.status === 429 || res.status >= 500) return 'retry';
    return 'rejected';
  },

  beacon(body: string): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return false;
    }
    // `sendBeacon` cannot set headers, so the content type has to ride on the
    // Blob. It sends same-origin cookies, which is the whole reason the
    // endpoint authenticates with the participant cookie rather than a bearer
    // token the page would have to attach itself.
    return navigator.sendBeacon(PATH, new Blob([body], { type: 'application/json' }));
  },
};

/** Same ceiling as telemetry, for the same reason: a page nobody is watching
 *  must not grow a buffer forever. */
const MAX_QUEUE = 200;
const FLUSH_INTERVAL_MS = 5_000;
/** Room for `roomId`, `sessionId` and the braces around the items. */
const ENVELOPE_BYTES = 256;
/** How many batches one flush may send before letting the loop breathe. */
const MAX_BATCHES_PER_FLUSH = 4;
/**
 * Browsers budget beacon bytes per page, and a page that is being torn down
 * gets no second chance to find out it ran over. Two batches is roughly what
 * that budget allows; anything still queued after them was never going to
 * arrive.
 */
const MAX_BEACON_BATCHES = 2;
/**
 * The pause after a batch has been given up on. Without it, a server outage
 * turns every queued item into a request the moment the previous one fails —
 * a busy loop against a service that is already struggling.
 */
const PAUSE_AFTER_GIVING_UP_MS = 30_000;

export class Outbox {
  #roomId: string | null = null;
  #sessionId: string | null = null;
  #queue: IngestItem[] = [];
  #timer: ReturnType<typeof setInterval> | null = null;
  #scheduled = false;
  #flushing = false;
  #pausedUntil = 0;
  #dropped = 0;
  #transport: OutboxTransport;
  /**
   * One budget across batches, not one per batch. A server that is down
   * refuses every batch alike, so the backoff that matters is the one against
   * the server, and resetting it per batch would mean a fresh half-second
   * retry for each of two hundred queued items.
   */
  #budget: RetryBudget;
  #listening = false;

  constructor(transport: OutboxTransport = browserTransport) {
    this.#transport = transport;
    this.#budget = new RetryBudget({ totalMs: 120_000, baseMs: 1_000, maxMs: 20_000 });
  }

  /** Items queued before this are held, not dropped: the batch needs a room. */
  bind(roomId: string | null, sessionId: string | null = null): void {
    this.#roomId = roomId;
    this.#sessionId = sessionId;
  }

  start(intervalMs = FLUSH_INTERVAL_MS): void {
    this.#timer ??= setInterval(() => void this.flush(), intervalMs);
    this.#listen();
  }

  /**
   * Ends the call's use of the channel. The final send goes by beacon rather
   * than `fetch`, because `stop()` often runs in the same teardown that is
   * about to take the page with it.
   */
  stop(): void {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
    this.#unlisten();
    this.flushOnUnload();
  }

  /** Queued but not yet delivered. For tests and for a debugging overlay. */
  get pending(): number {
    return this.#queue.length;
  }

  /** Items thrown away — over the cap, unsendable, or retried to exhaustion. */
  get dropped(): number {
    return this.#dropped;
  }

  /**
   * One measured part of a call, as the client chose to divide it. Nothing
   * calls this yet: what a part is depends on what the provider turns out to
   * emit (task 1), and measuring one is task 2.
   */
  part(part: Omit<IngestPart, 'kind'>): void {
    this.#enqueue({ kind: 'part', ...part });
  }

  /**
   * A reference to an object the browser has already PUT to the bucket. The
   * bytes never come through here or through the server — only the key, the
   * size and the duration do.
   */
  recording(recording: Omit<IngestRecording, 'kind'>): void {
    this.#enqueue({ kind: 'recording', ...recording });
  }

  /**
   * Sends what it can, and returns having swallowed anything that went wrong.
   * Callers do not need to await it and nothing on the path of a call does.
   */
  async flush(): Promise<void> {
    if (this.#flushing || this.#roomId === null) return;
    if (Date.now() < this.#pausedUntil) return;
    this.#flushing = true;
    try {
      for (let sent = 0; sent < MAX_BATCHES_PER_FLUSH; sent += 1) {
        const batch = this.#takeBatch();
        if (!batch) return;

        const outcome = await this.#post(batch.body);
        if (outcome === 'delivered') {
          this.#budget.reset();
          continue;
        }
        if (outcome === 'rejected') {
          // A batch the server refuses on its merits is dropped, not retried:
          // the alternative is a malformed payload the queue can never be rid
          // of, blocking everything queued behind it forever.
          this.#budget.reset();
          this.#dropped += batch.items.length;
          continue;
        }

        const wait = this.#budget.next();
        if (wait === null) {
          // Two minutes of trying is enough to outlast a restart and not
          // enough to hold a call's worth of stale measurements. Drop this
          // batch and stop asking for a while.
          this.#budget.reset();
          this.#dropped += batch.items.length;
          this.#pausedUntil = Date.now() + PAUSE_AFTER_GIVING_UP_MS;
          return;
        }
        this.#requeue(batch.items);
        this.#pausedUntil = Date.now() + wait;
        return;
      }
    } finally {
      this.#flushing = false;
    }
  }

  /**
   * A transport that throws where it promised to resolve is a bug, but it is
   * not allowed to reach a caller — one of those callers is a React effect —
   * and it is indistinguishable from the network being down, so it is treated
   * as the same thing.
   */
  async #post(body: string): Promise<SendOutcome> {
    try {
      return await this.#transport.post(body);
    } catch {
      return 'retry';
    }
  }

  /**
   * The send for a page that is going away, and the reason this class exists
   * in the shape it does.
   *
   * A call that ends with the tab closing is exactly when the last and most
   * valuable payload is sitting in the queue — the one describing how the call
   * actually finished. `fetch` at that moment is a race the page usually
   * loses; `sendBeacon` hands the request to the browser, which delivers it
   * after the document is gone.
   *
   * Nothing is awaited, because there is nothing left to await with.
   */
  flushOnUnload(): void {
    if (this.#roomId === null) return;
    for (let sent = 0; sent < MAX_BEACON_BATCHES; sent += 1) {
      const batch = this.#takeBatch();
      if (!batch) return;
      if (!this.#beacon(batch.body)) {
        // Refused — over the browser's budget, or beacons are unavailable.
        // The page may well still be alive (a backgrounded tab often is), so
        // the items go back rather than being thrown away.
        this.#requeue(batch.items);
        return;
      }
    }
  }

  /** Same reasoning as `#post`, and it matters more here: this one runs while
   *  the page is being torn down, where an exception has nowhere to go. */
  #beacon(body: string): boolean {
    try {
      return this.#transport.beacon(body);
    } catch {
      return false;
    }
  }

  /**
   * Bounded, synchronous, and cheap: an array push and, at worst, a splice.
   * Serialising here instead would put the cost of a batch on whatever is
   * reporting — which by task 6 is an audio pipeline.
   */
  #enqueue(item: IngestItem): void {
    this.#queue.push(item);
    if (this.#queue.length > MAX_QUEUE) {
      // Same rule as telemetry: drop the oldest. Applied to retried items too,
      // deliberately — a payload that has already failed several times is the
      // one least likely ever to land.
      this.#dropped += this.#queue.length - MAX_QUEUE;
      this.#queue.splice(0, this.#queue.length - MAX_QUEUE);
    }
    if (this.#queue.length >= INGEST_MAX_ITEMS) this.#schedule();
  }

  /**
   * A full batch is worth sending before the timer comes round, but not on the
   * caller's stack. The zero delay is the point: it gets the work onto a later
   * turn, so a burst of `recording()` calls costs the caller nothing but the
   * pushes.
   */
  #schedule(): void {
    if (this.#scheduled) return;
    this.#scheduled = true;
    setTimeout(() => {
      this.#scheduled = false;
      void this.flush();
    }, 0);
  }

  /**
   * Fills one batch from the front of the queue, up to both ceilings.
   *
   * An item that could not fit in an empty batch is dropped here rather than
   * being carried forever: no amount of waiting will make it smaller, and
   * leaving it at the head of the queue would block everything behind it.
   */
  #takeBatch(): { body: string; items: IngestItem[] } | null {
    const items: IngestItem[] = [];
    let bytes = ENVELOPE_BYTES;

    while (this.#queue.length > 0 && items.length < INGEST_MAX_ITEMS) {
      const next = this.#queue[0]!;
      // +1 for the comma that will join it to the previous item.
      const size = byteLength(JSON.stringify(next)) + 1;
      if (size + ENVELOPE_BYTES > INGEST_MAX_BYTES) {
        this.#queue.shift();
        this.#dropped += 1;
        continue;
      }
      if (bytes + size > INGEST_MAX_BYTES) break;
      this.#queue.shift();
      items.push(next);
      bytes += size;
    }
    if (items.length === 0) return null;

    const batch: IngestBatch = {
      roomId: this.#roomId!,
      ...(this.#sessionId ? { sessionId: this.#sessionId } : {}),
      items,
    };
    return { body: JSON.stringify(batch), items };
  }

  /** Back to the front, in order, and then back under the cap. */
  #requeue(items: IngestItem[]): void {
    this.#queue.unshift(...items);
    if (this.#queue.length > MAX_QUEUE) {
      this.#dropped += this.#queue.length - MAX_QUEUE;
      this.#queue.splice(0, this.#queue.length - MAX_QUEUE);
    }
  }

  /**
   * `visibilitychange` is the event that actually fires when a tab is closed,
   * discarded or swapped away from on a phone; `beforeunload` and `unload` are
   * not delivered at all on mobile Safari, which is precisely the case where a
   * call ends by the phone being locked. `pagehide` covers the back/forward
   * cache, where the document survives but stops running.
   */
  #listen(): void {
    if (this.#listening || typeof document === 'undefined') return;
    this.#listening = true;
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    window.addEventListener('pagehide', this.#onPageHide);
  }

  #unlisten(): void {
    if (!this.#listening || typeof document === 'undefined') return;
    this.#listening = false;
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    window.removeEventListener('pagehide', this.#onPageHide);
  }

  /** A tab that is merely backgrounded may well come back, so this is a
   *  precautionary flush, not a farewell; anything the beacon refuses stays
   *  queued for the timer to try again. */
  #onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') return;
    this.flushOnUnload();
  };

  /** `pagehide` fires with the document still visible, so it gets no
   *  visibility test of its own. */
  #onPageHide = (): void => {
    this.flushOnUnload();
  };
}

/** One per page, bound to whichever room the call is in. */
export const outbox = new Outbox();
